import React, { useState, useEffect } from 'react';
import axiosInstance from '@/axios';
import Modal from '@/pages/Other/Modal';
import { Edit2, Plus, Search, Trash2 } from 'lucide-react';
import ReactQuill from 'react-quill';

const HomePreview = () => {
    const [homeBanner, setHomeBanner] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [SelectedHomeBanner, setSelectedHomeBanner] = useState(null);
    const [modalType, setModalType] = useState('create');
    const [uploadingFile, setUploadingFile] = useState(null); // file selected for upload
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadField, setUploadField] = useState(null);
    /* Old Design:
    const [formData, setFormData] = useState({
        id: '',
        title: '',
        description: '',
        imageUrl: '',
    });
    */
    const [formData, setFormData] = useState({
        id: '',
        title: '',
        description: '',
        imageUrl: '',
        badgeText: '',
        originalPrice: '',
        discountedPrice: '',
        billingTerm: '',
        ctaText: '',
        ctaLink: '',
        backgroundColor: '',
        offerType: 'sunset-logos',
        category: '',
        offerColor: '',
        backgroundTemplate: 'gradient',
        backgroundTemplateUrl: '',
        gradientIntensity: 80
    });

    useEffect(() => {
        fetchHomePreview();
    }, []);

    const fetchHomePreview = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/public/home/banner');
            console.log('Home Banner Data:', response.data);
            setHomeBanner(response?.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch homebanner:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredHomebanner = homeBanner.filter(homeanner =>
        homeanner.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        homeanner.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    const handleQuillChange = (value) => {
        setFormData(prev => ({
            ...prev,
            description: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (modalType === 'create') {
                await axiosInstance.post('/public/home/banner', formData);
            } else if (modalType === 'edit') {
                await axiosInstance.put(`/public/home/banner/${formData.id}`, formData);
            }

            await fetchHomePreview();
            resetForm();
            setShowModal(false);
        } catch (error) {
            console.error('Error submitting homepreview:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (homebanner) => {
        setFormData(homebanner);
        setSelectedHomeBanner(homebanner);
        setModalType('edit');
        setShowModal(true);
    };

    const handleView = (homebanner) => {
        setSelectedHomeBanner(homebanner);
        setModalType('view');
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this Homebanner?')) return;

        setLoading(true);
        try {
            await axiosInstance.delete(`/public/home/banner/${id}`);
            setHomeBanner(prev => prev.filter(banner => banner.id !== id));
        } catch (error) {
            console.error('Error deleting Homebanner:', error);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        /* Old Design:
        setFormData({
            id: '',
            title: '',
            description: '',
            imageUrl: ''
        });
        */
        setFormData({
            id: '',
            title: '',
            description: '',
            imageUrl: '',
            badgeText: '',
            originalPrice: '',
            discountedPrice: '',
            billingTerm: '',
            ctaText: '',
            ctaLink: '',
            backgroundColor: '',
            offerType: 'sunset-logos',
            category: '',
            offerColor: '',
            backgroundTemplate: 'gradient',
            backgroundTemplateUrl: '',
            gradientIntensity: 80
        });
        setSelectedHomeBanner(null);
    };

    const openCreateModal = () => {
        resetForm();
        setModalType('create');
        setShowModal(true);
    };

    const handleFileSelect = (e, fieldName) => {
        const file = e.target.files[0];
        if (file) {
            setUploadingFile(file);
            setUploadField(fieldName);
        }
    };
    const handleUploadFile = async () => {
        if (!uploadingFile || !uploadField) return;

        setUploadLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadingFile);

            const res = await axiosInstance.post('/image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (res.data && res.data.url) {
                // Set the returned URL into the correct form field
                setFormData(prev => ({
                    ...prev,
                    [uploadField]: res.data.url,
                }));

                // Clear upload state
                setUploadingFile(null);
                setUploadField(null);
            } else {
                alert('Upload failed: no URL returned');
            }
        } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload file');
        } finally {
            setUploadLoading(false);
        }
    };

    const parseGradient = (gradientStr) => {
        if (!gradientStr) return { color1: '#1b0a91', color2: '#0d043d', angle: 135 };
        const hexMatches = gradientStr.match(/#[0-9a-fA-F]{3,6}/g);
        const angleMatch = gradientStr.match(/(\d+)deg/);
        
        return {
            color1: hexMatches && hexMatches[0] ? hexMatches[0] : '#1b0a91',
            color2: hexMatches && hexMatches[1] ? hexMatches[1] : (hexMatches && hexMatches[0] ? hexMatches[0] : '#0d043d'),
            angle: angleMatch && angleMatch[1] ? parseInt(angleMatch[1]) : 135
        };
    };

    const updateGradient = (c1, c2, deg) => {
        setFormData(prev => ({
            ...prev,
            backgroundColor: `linear-gradient(${deg}deg, ${c1} 0%, ${c2} 100%)`
        }));
    };

    const updateOfferGradient = (c1, c2, deg) => {
        setFormData(prev => ({
            ...prev,
            offerColor: `linear-gradient(${deg}deg, ${c1} 0%, ${c2} 100%)`
        }));
    };

    return (
        // <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
        <div className="container mx-auto px-5 mt-3">
            {/* Header */}
            <h1 className="text-2xl font-bold mb-4 p-4 bg-[#264b82] text-white rounded-md mt-0">
                Home Banner Management
            </h1>

            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative flex-1 max-w-md">
                    <Search className="!absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                        type="text"
                        placeholder="Search home title..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full !pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                </div>
                <button
                    onClick={openCreateModal}
                    className="bg-[#264b82] text-white px-6 py-3 rounded-xl hover:from-indigo-700  flex items-center gap-2 shadow-lg hover:shadow-xl"
                >
                    <Plus className="h-5 w-5" />
                    Add HomeBanner
                </button>
            </div>

            {/* HomeBanner Table */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b">
                    <h2 className="text-xl font-semibold text-gray-800">All HomeBanner</h2>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    </div>
                ) : (
                    <div className="instructor-table overflow-x-auto">
                        <table className="table-width-100">
                            <thead className="">
                                <tr>
                                    <th className=" uppercase">ID</th>
                                    <th className=" uppercase">Title</th>
                                    <th className=" uppercase">Description</th>
                                    <th className=" uppercase">Image</th>
                                    <th className=" uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredHomebanner.map((homebanner) => (
                                    <tr key={homebanner.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="cell">{homebanner.id}</td>
                                        <td className="cell">{homebanner.title}</td>
                                        <td className="cell">{homebanner.description}</td>
                                        <td className="cell">
                                            <img
                                                src={homebanner.imageUrl}
                                                alt={homebanner.title}
                                                className="w-16 h-16 object-cover rounded-lg"
                                            />
                                        </td>

                                        <td className="cell">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleEdit(homebanner)}
                                                    className="p-2 text-[#264b82] hover:bg-indigo-50 rounded-lg transition-colors"
                                                    title="Edit"
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(homebanner.id)}
                                                    className="p-2 text-[#264b82] hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {filteredHomebanner.length === 0 && (
                            <div className="text-center py-12">
                                <p className="!text-gray-500 text-lg">No data found</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
                    <>
                        <div className="bg-[#264b82] px-6 py-4 text-white">
                            <h3 className="text-xl font-semibold">
                                {modalType === 'create' && 'Add New Expert'}
                                {modalType === 'edit' && 'Edit Expert'}
                                {modalType === 'view' && 'View Expert'}
                            </h3>
                        </div>

                        <div className="p-6">
                            {modalType === 'view' ? (
                                /* Old Design:
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                        <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">{SelectedHomeBanner?.title}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                        <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">{SelectedHomeBanner?.description}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                                        <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">
                                            <img
                                                src={SelectedHomeBanner?.imageUrl}
                                                alt={SelectedHomeBanner?.title}
                                                className="w-16 h-16 object-cover rounded-lg"
                                            />
                                        </p>
                                    </div>
                                </div>
                                */
                                <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                            <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">{SelectedHomeBanner?.title}</p>
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                            <div className="!text-gray-900 bg-gray-50 p-3 rounded-lg prose max-w-none" dangerouslySetInnerHTML={{ __html: SelectedHomeBanner?.description }} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                            <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">{SelectedHomeBanner?.category || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Offer Layout Type</label>
                                            <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">{SelectedHomeBanner?.offerType || 'sunset-logos'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                                            <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">{SelectedHomeBanner?.badgeText || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Billing Term</label>
                                            <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">{SelectedHomeBanner?.billingTerm || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Original Price</label>
                                            <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">{SelectedHomeBanner?.originalPrice || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Discounted Price</label>
                                            <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">{SelectedHomeBanner?.discountedPrice || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                                            <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">{SelectedHomeBanner?.ctaText || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">CTA Redirect Link</label>
                                            <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg">{SelectedHomeBanner?.ctaLink || 'N/A'}</p>
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Background Gradient/Color</label>
                                            <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg font-mono">{SelectedHomeBanner?.backgroundColor || 'N/A'}</p>
                                         </div>
                                         <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Gradient Overlay Intensity</label>
                                             <p className="!text-gray-900 bg-gray-50 p-3 rounded-lg font-mono">{SelectedHomeBanner?.gradientIntensity !== undefined ? `${SelectedHomeBanner?.gradientIntensity}%` : '80%'}</p>
                                         </div>
                                         <div className="col-span-2">
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Background Template Image</label>
                                             <div className="!text-gray-900 bg-gray-50 p-3 rounded-lg flex items-center gap-4">
                                                 {SelectedHomeBanner?.backgroundTemplateUrl ? (
                                                     <img
                                                         src={SelectedHomeBanner?.backgroundTemplateUrl}
                                                         alt="Background template"
                                                         className="w-24 h-24 object-cover rounded-lg shadow-md"
                                                     />
                                                 ) : (
                                                     <span>No Background Template Image</span>
                                                 )}
                                             </div>
                                         </div>
                                        <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Right-Side Artwork/Image</label>
                                            <div className="!text-gray-900 bg-gray-50 p-3 rounded-lg flex items-center gap-4">
                                                {SelectedHomeBanner?.imageUrl ? (
                                                    <img
                                                        src={SelectedHomeBanner?.imageUrl}
                                                        alt={SelectedHomeBanner?.title}
                                                        className="w-24 h-24 object-cover rounded-lg shadow-md"
                                                    />
                                                ) : (
                                                    <span>No Image</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                                            <input
                                                type="text"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                                            {/* <input
                                                type="text"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                            /> */}
                                            <div className="texteditor-hero">
                                                <ReactQuill
                                                    value={formData.description}
                                                    onChange={handleQuillChange}
                                                    modules={{
                                                        toolbar: [
                                                            [{ header: [1, 2,3,4, false] }],
                                                            ["bold", "italic", "underline", "strike"],
                                                            [{ list: "ordered" }, { list: "bullet" }],
                                                            ["link"],
                                                        ],
                                                    }}
                                                    placeholder="Write your description..."
                                                />
                                            </div>
                                        </div>
                                    </div>


                                    <div>
                                     {/* Old Design:
                                     <div>
                                         <label className="text-sm font-medium">HomeBanner Image URL</label>
                                         <input readOnly={true} name="imageUrl" value={formData.imageUrl} onChange={handleInputChange}
                                             placeholder='upload image'
                                             className="w-full px-3 py-2 border  rounded mb-2" />
                                         <input
                                             type="file"
                                             accept="image/*"
                                             onChange={(e) => handleFileSelect(e, 'imageUrl')}
                                             className="mb-2"
                                         />
                                         {uploadingFile && uploadField === 'imageUrl' && (
                                             <button
                                                 type="button"
                                                 onClick={handleUploadFile}
                                                 disabled={uploadLoading}
                                                 className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                             >
                                                 {uploadLoading ? "Uploading..." : `Upload ${uploadingFile.name}`}
                                             </button>
                                         )}
                                     </div>
                                     */}
                                     
                                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
                                         <div className="col-span-2">
                                             <h4 className="font-semibold text-gray-800 text-base">Categories, Offers, and Deals (New Design)</h4>
                                         </div>
                                         
                                         <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Category Name / Tag</label>
                                             <input
                                                 type="text"
                                                 name="category"
                                                 placeholder="e.g. Web Development, Business"
                                                 value={formData.category || ''}
                                                 onChange={handleInputChange}
                                                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                             />
                                         </div>

                                         <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Offer Layout Type</label>
                                             <select
                                                 name="offerType"
                                                 value={formData.offerType || 'sunset-logos'}
                                                 onChange={handleInputChange}
                                                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                             >
                                                 <option value="sunset-logos">Sunset + Partner Logos</option>
                                                 <option value="circle-image">Circular Photo + Offer Badge</option>
                                             </select>
                                         </div>

                                         <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Background Template / Artwork Design</label>
                                             <select
                                                 name="backgroundTemplate"
                                                 value={formData.backgroundTemplate || 'gradient'}
                                                 onChange={handleInputChange}
                                                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                             >
                                                 <option value="gradient">Simple Solid/Gradient</option>
                                                 <option value="grid">Modern Blueprint Grid</option>
                                                 <option value="dots">Modern Tech Dotted Pattern</option>
                                                 <option value="mesh">Glowing Ambient Mesh Circles</option>
                                                 <option value="image">Custom Uploaded Image</option>
                                             </select>
                                         </div>

                                         <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Background Template Image (Optional)</label>
                                             <input readOnly={true} name="backgroundTemplateUrl" value={formData.backgroundTemplateUrl || ''} onChange={handleInputChange}
                                                 placeholder='Upload background template image'
                                                 className="w-full px-3 py-2 border rounded-lg mb-2 focus:ring-2 focus:ring-indigo-500 font-mono text-sm" />
                                             <input
                                                 type="file"
                                                 accept="image/*"
                                                 onChange={(e) => handleFileSelect(e, 'backgroundTemplateUrl')}
                                                 className="mb-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                                             />
                                             {uploadingFile && uploadField === 'backgroundTemplateUrl' && (
                                                 <button
                                                     type="button"
                                                     onClick={handleUploadFile}
                                                     disabled={uploadLoading}
                                                     className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                                                 >
                                                     {uploadLoading ? "Uploading..." : `Upload ${uploadingFile.name}`}
                                                 </button>
                                             )}
                                         </div>

                                         <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text (Hot Deal/Offer)</label>
                                             <input
                                                 type="text"
                                                 name="badgeText"
                                                 placeholder="e.g. OFFER ENDS SOON or 40% OFF"
                                                 value={formData.badgeText || ''}
                                                 onChange={handleInputChange}
                                                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                             />
                                         </div>

                                         <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Billing Term / Offer Detail</label>
                                             <input
                                                 type="text"
                                                 name="billingTerm"
                                                 placeholder="e.g. /year or team training"
                                                 value={formData.billingTerm || ''}
                                                 onChange={handleInputChange}
                                                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                             />
                                         </div>

                                         <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Original Price (Strikethrough)</label>
                                             <input
                                                 type="text"
                                                 name="originalPrice"
                                                 placeholder="e.g. 13,999 or ₹13,999"
                                                 value={formData.originalPrice || ''}
                                                 onChange={handleInputChange}
                                                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                             />
                                         </div>

                                         <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Discounted Price (Bold highlight)</label>
                                             <input
                                                 type="text"
                                                 name="discountedPrice"
                                                 placeholder="e.g. 7,499 or ₹7,499"
                                                 value={formData.discountedPrice || ''}
                                                 onChange={handleInputChange}
                                                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                             />
                                         </div>

                                         <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                                             <input
                                                 type="text"
                                                 name="ctaText"
                                                 placeholder="e.g. Save now"
                                                 value={formData.ctaText || ''}
                                                 onChange={handleInputChange}
                                                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                             />
                                         </div>

                                         <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">CTA Redirect Link</label>
                                             <input
                                                 type="text"
                                                 name="ctaLink"
                                                 placeholder="e.g. /login or /forbusiness"
                                                 value={formData.ctaLink || ''}
                                                 onChange={handleInputChange}
                                                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                             />
                                         </div>

                                         {/* Old Design:
                                         <div className="col-span-2">
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Background Gradient/Color Style (CSS)</label>
                                             <input
                                                 type="text"
                                                 name="backgroundColor"
                                                 placeholder="e.g. linear-gradient(135deg, #1f0a5c 0%, #10063b 100%)"
                                                 value={formData.backgroundColor || ''}
                                                 onChange={handleInputChange}
                                                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono"
                                             />
                                         </div>
                                         */}

                                         {/* Old Design:
                                         <div className="col-span-2">
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Background Gradient/Color Style (CSS)</label>
                                             <div className="flex gap-4 items-center">
                                                 <input
                                                     type="text"
                                                     name="backgroundColor"
                                                     placeholder="e.g. linear-gradient(135deg, #1f0a5c 0%, #10063b 100%)"
                                                     value={formData.backgroundColor || ''}
                                                     onChange={handleInputChange}
                                                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono flex-1"
                                                 />
                                                 <div 
                                                     className="w-24 h-11 rounded-lg border-2 border-white shadow-md flex items-center justify-center text-[10px] text-white font-bold select-none text-center"
                                                     style={{
                                                         background: formData.backgroundColor || 'linear-gradient(135deg, #1e1b4b 0%, #311042 100%)'
                                         </div>
                                         */}

                                         {/* Old Design:
                                         <div className="col-span-2">
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Background Gradient/Color Style (CSS)</label>
                                             <div className="flex gap-4 items-center">
                                                 <input
                                                     type="text"
                                                     name="backgroundColor"
                                                     placeholder="e.g. linear-gradient(135deg, #1f0a5c 0%, #10063b 100%)"
                                                     value={formData.backgroundColor || ''}
                                                     onChange={handleInputChange}
                                                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono flex-1 text-sm"
                                                 />
                                                 <div 
                                                     className="w-24 h-11 rounded-lg border-2 border-white shadow-md flex items-center justify-center text-[10px] text-white font-bold select-none text-center"
                                                     style={{
                                                         background: formData.backgroundColor || 'linear-gradient(135deg, #1e1b4b 0%, #311042 100%)'
                                                     }}
                                                 >
                                                     Color Preview
                                                 </div>
                                             </div>

                                             {(() => {
                                                 const { color1, color2, angle } = parseGradient(formData.backgroundColor);
                                                 return (
                                                     <div className="mt-3 bg-gray-50 p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row gap-4 items-center justify-between shadow-sm">
                                                         <div className="flex gap-2.5 items-center">
                                                             <label className="text-xs font-bold text-gray-600">Color 1:</label>
                                                             <input 
                                                                 type="color" 
                                                                 value={color1} 
                                                                 onChange={(e) => updateGradient(e.target.value, color2, angle)} 
                                                                 className="w-7 h-7 rounded cursor-pointer border border-gray-300 p-0 overflow-hidden"
                                                             />
                                                             <span className="text-[11px] font-mono text-gray-500">{color1}</span>
                                                         </div>
                                                         
                                                         <div className="flex gap-2.5 items-center">
                                                             <label className="text-xs font-bold text-gray-600">Color 2:</label>
                                                             <input 
                                                                 type="color" 
                                                                 value={color2} 
                                                                 onChange={(e) => updateGradient(color1, e.target.value, angle)} 
                                                                 className="w-7 h-7 rounded cursor-pointer border border-gray-300 p-0 overflow-hidden"
                                                             />
                                                             <span className="text-[11px] font-mono text-gray-500">{color2}</span>
                                                         </div>
                                                         
                                                         <div className="flex gap-2.5 items-center flex-1 w-full max-w-[180px]">
                                                             <label className="text-xs font-bold text-gray-600 whitespace-nowrap">Angle ({angle}°):</label>
                                                             <input 
                                                                 type="range" 
                                                                 min="0" 
                                                                 max="360" 
                                                                 value={angle} 
                                                                 onChange={(e) => updateGradient(color1, color2, e.target.value)} 
                                                                 className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                                             />
                                                         </div>
                                                     </div>
                                                 );
                                             })()}
                                             
                                             <div className="mt-3 flex flex-wrap gap-2 items-center">
                                                 <span className="text-xs text-gray-500">Quick presets:</span>
                                                 {[
                                                     { name: 'Sunset Indigo', value: 'linear-gradient(135deg, #1b0a91 0%, #0d043d 100%)' },
                                                     { name: 'Ocean Blue', value: 'linear-gradient(135deg, #0250c5 0%, #043585 100%)' },
                                                     { name: 'Royal Velvet', value: 'linear-gradient(135deg, #3b0764 0%, #0f051d 100%)' },
                                                     { name: 'Midnight Rose', value: 'linear-gradient(135deg, #500724 0%, #14020a 100%)' },
                                                     { name: 'Forest Green', value: 'linear-gradient(135deg, #064e3b 0%, #022c22 100%)' }
                                                 ].map((preset, idx) => (
                                                     <button
                                                         key={idx}
                                                         type="button"
                                                         onClick={() => setFormData(prev => ({ ...prev, backgroundColor: preset.value }))}
                                                         className="text-[10px] px-2.5 py-1.5 rounded border bg-gray-50 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 text-gray-700 font-medium shadow-sm transition-all duration-200"
                                                     >
                                                         {preset.name}
                                                     </button>
                                                 ))}
                                             </div>
                                         </div>
                                         */}

                                         <div className="col-span-2">
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Background Gradient/Color Style (CSS)</label>
                                             <div className="flex gap-4 items-center">
                                                 <input
                                                     type="text"
                                                     name="backgroundColor"
                                                     placeholder="e.g. linear-gradient(135deg, #1f0a5c 0%, #10063b 100%)"
                                                     value={formData.backgroundColor || ''}
                                                     onChange={handleInputChange}
                                                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono flex-1 text-sm"
                                                 />
                                                 {/* Visual live color mix preview */}
                                                 <div 
                                                     className="w-24 h-11 rounded-lg border-2 border-white shadow-md flex items-center justify-center text-[10px] text-white font-bold select-none text-center"
                                                     style={{
                                                         background: formData.backgroundColor || 'linear-gradient(135deg, #1e1b4b 0%, #311042 100%)'
                                                     }}
                                                 >
                                                     Color Preview
                                                 </div>
                                             </div>

                                             {/* Color selector inputs (Interactive mix) */}
                                             {(() => {
                                                 const { color1, color2, angle } = parseGradient(formData.backgroundColor);
                                                 return (
                                                     <div className="mt-3 bg-gray-50 p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row gap-4 items-center justify-between shadow-sm">
                                                         <div className="flex gap-2.5 items-center">
                                                             <label className="text-xs font-bold text-gray-600">Color 1:</label>
                                                             <input 
                                                                 type="color" 
                                                                 value={color1} 
                                                                 onChange={(e) => updateGradient(e.target.value, color2, angle)} 
                                                                 className="w-7 h-7 rounded cursor-pointer border border-gray-300 p-0 overflow-hidden"
                                                             />
                                                             <span className="text-[11px] font-mono text-gray-500">{color1}</span>
                                                         </div>
                                                         
                                                         <div className="flex gap-2.5 items-center">
                                                             <label className="text-xs font-bold text-gray-600">Color 2:</label>
                                                             <input 
                                                                 type="color" 
                                                                 value={color2} 
                                                                 onChange={(e) => updateGradient(color1, e.target.value, angle)} 
                                                                 className="w-7 h-7 rounded cursor-pointer border border-gray-300 p-0 overflow-hidden"
                                                             />
                                                             <span className="text-[11px] font-mono text-gray-500">{color2}</span>
                                                         </div>
                                                         
                                                         <div className="flex gap-2.5 items-center flex-1 w-full max-w-[180px]">
                                                             <label className="text-xs font-bold text-gray-600 whitespace-nowrap">Angle ({angle}°):</label>
                                                             <input 
                                                                 type="range" 
                                                                 min="0" 
                                                                 max="360" 
                                                                 value={angle} 
                                                                 onChange={(e) => updateGradient(color1, color2, e.target.value)} 
                                                                 className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                                             />
                                                         </div>
                                                     </div>
                                                 );
                                              })()}

                                              {/* Preset buttons */}
                                              <div className="mt-3 flex flex-wrap gap-2 items-center">
                                                  <span className="text-xs text-gray-500">Quick presets:</span>
                                                  {[
                                                      { name: 'Sunset Indigo', value: 'linear-gradient(135deg, #1b0a91 0%, #0d043d 100%)' },
                                                      { name: 'Ocean Blue', value: 'linear-gradient(135deg, #0250c5 0%, #043585 100%)' },
                                                      { name: 'Royal Velvet', value: 'linear-gradient(135deg, #3b0764 0%, #0f051d 100%)' },
                                                      { name: 'Midnight Rose', value: 'linear-gradient(135deg, #500724 0%, #14020a 100%)' },
                                                      { name: 'Forest Green', value: 'linear-gradient(135deg, #064e3b 0%, #022c22 100%)' }
                                                  ].map((preset, idx) => (
                                                      <button
                                                          key={idx}
                                                          type="button"
                                                          onClick={() => setFormData(prev => ({ ...prev, backgroundColor: preset.value }))}
                                                          className="text-[10px] px-2.5 py-1.5 rounded border bg-gray-50 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 text-gray-700 font-medium shadow-sm transition-all duration-200"
                                                      >
                                                          {preset.name}
                                                      </button>
                                                  ))}
                                              </div>

                                              <div className="mt-4 border-t pt-4">
                                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                                      Gradient Overlay Intensity: {formData.gradientIntensity !== undefined ? formData.gradientIntensity : 80}%
                                                  </label>
                                                  <div className="flex items-center gap-4">
                                                      <input 
                                                          type="range" 
                                                          name="gradientIntensity"
                                                          min="0" 
                                                          max="100" 
                                                          value={formData.gradientIntensity !== undefined ? formData.gradientIntensity : 80} 
                                                          onChange={(e) => setFormData(prev => ({ ...prev, gradientIntensity: parseInt(e.target.value) }))} 
                                                          className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                                      />
                                                      <span className="text-xs font-semibold text-gray-500 w-10 text-right">
                                                          {formData.gradientIntensity !== undefined ? formData.gradientIntensity : 80}%
                                                      </span>
                                                  </div>
                                                  <p className="text-[11px] text-gray-500 mt-1">Adjust the opacity of the gradient overlay over the background pattern or template image.</p>
                                              </div>
                                          </div>

                                          <div className="col-span-2 border-t pt-4">
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Offer Box Color / Gradient Style (CSS)</label>
                                             <div className="flex gap-4 items-center">
                                                 <input
                                                     type="text"
                                                     name="offerColor"
                                                     placeholder="e.g. linear-gradient(135deg, #fbbf24 0%, #f97316 100%)"
                                                     value={formData.offerColor || ''}
                                                     onChange={handleInputChange}
                                                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono flex-1 text-sm"
                                                 />
                                                 {/* Visual live color preview for offer box */}
                                                 <div 
                                                     className="w-24 h-11 rounded-lg border-2 border-white shadow-md flex items-center justify-center text-[10px] text-white font-bold select-none text-center"
                                                     style={{
                                                         background: formData.offerColor || 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)'
                                                     }}
                                                 >
                                                     Box Preview
                                                 </div>
                                             </div>

                                             {/* Color selector inputs for Offer Box */}
                                             {(() => {
                                                 const { color1, color2, angle } = parseGradient(formData.offerColor || 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)');
                                                 return (
                                                     <div className="mt-3 bg-gray-50 p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row gap-4 items-center justify-between shadow-sm">
                                                         <div className="flex gap-2.5 items-center">
                                                             <label className="text-xs font-bold text-gray-600">Color 1:</label>
                                                             <input 
                                                                 type="color" 
                                                                 value={color1} 
                                                                 onChange={(e) => updateOfferGradient(e.target.value, color2, angle)} 
                                                                 className="w-7 h-7 rounded cursor-pointer border border-gray-300 p-0 overflow-hidden"
                                                             />
                                                             <span className="text-[11px] font-mono text-gray-500">{color1}</span>
                                                         </div>
                                                         
                                                         <div className="flex gap-2.5 items-center">
                                                             <label className="text-xs font-bold text-gray-600">Color 2:</label>
                                                             <input 
                                                                 type="color" 
                                                                 value={color2} 
                                                                 onChange={(e) => updateOfferGradient(color1, e.target.value, angle)} 
                                                                 className="w-7 h-7 rounded cursor-pointer border border-gray-300 p-0 overflow-hidden"
                                                             />
                                                             <span className="text-[11px] font-mono text-gray-500">{color2}</span>
                                                         </div>
                                                         
                                                         <div className="flex gap-2.5 items-center flex-1 w-full max-w-[180px]">
                                                             <label className="text-xs font-bold text-gray-600 whitespace-nowrap">Angle ({angle}°):</label>
                                                             <input 
                                                                 type="range" 
                                                                 min="0" 
                                                                 max="360" 
                                                                 value={angle} 
                                                                 onChange={(e) => updateOfferGradient(color1, color2, e.target.value)} 
                                                                 className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                                             />
                                                         </div>
                                                     </div>
                                                 );
                                             })()}

                                             {/* Quick presets for Offer Box */}
                                             <div className="mt-3 flex flex-wrap gap-2 items-center">
                                                 <span className="text-xs text-gray-500">Quick Box presets:</span>
                                                 {[
                                                     { name: 'Warm Sunset (Orange)', value: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #dc2626 100%)' },
                                                     { name: 'Neon Pink-Violet', value: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)' },
                                                     { name: 'Cyber Cyan-Blue', value: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)' },
                                                     { name: 'Golden Glow', value: 'linear-gradient(135deg, #fef08a 0%, #eab308 100%)' }
                                                 ].map((preset, idx) => (
                                                     <button
                                                         key={idx}
                                                         type="button"
                                                         onClick={() => setFormData(prev => ({ ...prev, offerColor: preset.value }))}
                                                         className="text-[10px] px-2.5 py-1.5 rounded border bg-gray-50 hover:bg-orange-50 hover:text-orange-700 hover:border-orange-300 text-gray-700 font-medium shadow-sm transition-all duration-200"
                                                     >
                                                         {preset.name}
                                                     </button>
                                                 ))}
                                             </div>
                                         </div>

                                         <div className="col-span-2 border-t pt-4">
                                             <label className="block text-sm font-medium text-gray-700 mb-1">Right-Side Artwork/Image</label>
                                             <input readOnly={true} name="imageUrl" value={formData.imageUrl || ''} onChange={handleInputChange}
                                                 placeholder='upload image'
                                                 className="w-full px-3 py-2 border rounded-lg mb-2 focus:ring-2 focus:ring-indigo-500" />
                                             <input
                                                 type="file"
                                                 accept="image/*"
                                                 onChange={(e) => handleFileSelect(e, 'imageUrl')}
                                                 className="mb-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                                             />
                                             {uploadingFile && uploadField === 'imageUrl' && (
                                                 <button
                                                     type="button"
                                                     onClick={handleUploadFile}
                                                     disabled={uploadLoading}
                                                     className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                                                 >
                                                     {uploadLoading ? "Uploading..." : `Upload ${uploadingFile.name}`}
                                                 </button>
                                             )}
                                         </div>
                                     </div></div>


                                    {/* {modalType === 'edit' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                            <select
                                                name="status"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                            >
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>
                                    )} */}
                                </div>
                            )}

                            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    {modalType === 'view' ? 'Close' : 'Cancel'}
                                </button>
                                {modalType !== 'view' && (
                                    <button
                                        type="submit"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        className="px-6 py-2 bg-[#264b82] text-white rounded-lg  disabled:opacity-50"
                                    >
                                        {loading ? 'Saving...' : modalType === 'create' ? 'Create' : 'Update'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </>
                </Modal>
            )}
        </div>
        // </div>
    );
};

export default HomePreview;